<template>
  <div>
    <div class="d-flex flex-wrap align-items-center mb-2">
      <h4 class="mb-0 mr-2">
        {{ PAGE_TITLE }}
      </h4>
      <b-breadcrumb
        class="mb-0"
        :items="BREADCRUMB_ITEMS"
      />
    </div>
    <b-alert
      v-if="errorText"
      show
      variant="danger"
    >
      {{ errorText }}
    </b-alert>
    <b-card
      v-if="!forbidden"
      no-body
    >
      <b-form
        class="px-1 pt-1"
        @submit.prevent="applyFilters"
      >
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              label="Da:"
              label-for="report-date-from"
            >
              <b-form-datepicker
                id="report-date-from"
                v-model="filters.dateFrom"
                v-bind="DATEPICKER_PROPS"
                :disabled="loadingShow"
                :state="rangeErrors.dateFrom ? false : null"
              />
              <div
                v-if="rangeErrors.dateFrom"
                class="invalid-feedback d-block"
              >
                {{ rangeErrors.dateFrom }}
              </div>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              label="A:"
              label-for="report-date-to"
            >
              <b-form-datepicker
                id="report-date-to"
                v-model="filters.dateTo"
                v-bind="DATEPICKER_PROPS"
                :disabled="loadingShow"
                :min="filters.dateFrom"
                :state="rangeErrors.dateTo ? false : null"
              />
              <div
                v-if="rangeErrors.dateTo"
                class="invalid-feedback d-block"
              >
                {{ rangeErrors.dateTo }}
              </div>
            </b-form-group>
          </b-col>
          <b-col
            class="d-flex align-items-end pb-1"
            cols="12"
            md="4"
          >
            <b-button
              :disabled="loadingShow"
              type="submit"
              variant="primary"
            >
              Applica
            </b-button>
            <b-button
              class="ml-1"
              :disabled="loadingShow"
              variant="outline-secondary"
              @click="resetFilters"
            >
              Azzera
            </b-button>
          </b-col>
        </b-row>
      </b-form>
      <div class="position-relative">
        <MyLoading :loading-show="loadingShow" />
        <b-table
          hover
          responsive
          :fields="TABLE_FIELDS"
          :items="rows"
          primary-key="id"
          :show-empty="hasLoaded"
        >
          <template #empty>
            <p class="text-center py-2 mb-0">
              {{ EMPTY_TEXT }}
            </p>
          </template>
          <template #cell(label)="data">
            <span
              class="deleted-report-name text-truncate d-inline-block"
              :title="getLabelText(data.item)"
            >
              {{ getLabelText(data.item) }}
            </span>
          </template>
          <template #cell(deleted_at)="data">
            {{ formatDeletedAt(data.item.deleted_at) }}
          </template>
          <template #cell(causer_username)="data">
            {{ getCauserText(data.item) }}
          </template>
          <template #cell(deletion_type)="data">
            <b-badge
              v-if="getTypeBadge(data.item)"
              :variant="getTypeBadge(data.item).variant"
            >
              {{ getTypeBadge(data.item).text }}
            </b-badge>
            <span v-else>
              {{ EM_DASH }}
            </span>
          </template>
          <template #cell(actions)="data">
            <template v-if="isPermanentlyDeleted(data.item)">
              <b-badge variant="dark">
                {{ PERMANENT_LABEL }}
              </b-badge>
              <span
                v-b-tooltip.hover
                :aria-label="RETENTION_TOOLTIP"
                class="ml-50"
                tabindex="0"
                :title="RETENTION_TOOLTIP"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="14"
                />
              </span>
            </template>
            <b-button
              v-else-if="restoreMode(data.item) === 'original'"
              :disabled="restoreBusy || loadingShow"
              size="sm"
              variant="outline-primary"
              @click="onRestoreClick(data.item)"
            >
              Ripristina
            </b-button>
            <b-button
              v-else-if="restoreMode(data.item) === 'choose'"
              :disabled="restoreBusy || loadingShow"
              size="sm"
              :title="RESTORE_CHOOSE_HINT"
              variant="outline-primary"
              @click="onChooseDestination(data.item)"
            >
              Ripristina in…
            </b-button>
            <span
              v-else
              class="text-muted"
            >
              {{ EM_DASH }}
            </span>
          </template>
        </b-table>
      </div>
      <div
        v-if="hasLoaded"
        class="d-flex justify-content-center align-items-center mt-1 pb-1"
      >
        <b-button
          :disabled="!canPrev"
          variant="outline-secondary"
          @click="goPrev"
        >
          Precedente
        </b-button>
        <span class="mx-2 text-muted">
          Pagina {{ pageNumber }}
        </span>
        <b-button
          :disabled="!canNext"
          variant="outline-secondary"
          @click="goNext"
        >
          Successiva
        </b-button>
      </div>
    </b-card>
    <MoveModal
      :id="RESTORE_DESTINATION_MODAL_ID"
      :trashed="true"
    />
  </div>
</template>

<script>
import {
  BAlert, BBadge, BBreadcrumb, BButton, BCard, BCol, BForm, BFormDatepicker, BFormGroup, BRow, BTable, VBTooltip,
} from 'bootstrap-vue'
// The router lazy-loads this view and the repository chain imports the router: a cycle only at load time.
// eslint-disable-next-line import/no-cycle
import objectsRepository from '@/api/objectsRepository'
import MyLoading from '@/layouts/components/MyLoading.vue'
import MoveModal from '@/layouts/components/modals/MoveModal.vue'
import restoreResults from '@/helpers/restore-results'
import useMyStore from '@/services/useMyStore'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BREADCRUMB_ITEMS,
  DATEPICKER_PROPS,
  DATE_FORMAT,
  EM_DASH,
  EMPTY_TEXT,
  LOAD_ERROR_OVERRIDES,
  PAGE_TITLE,
  PERMANENT_LABEL,
  RESTORE_CHOOSE_HINT,
  RESTORE_CONFIRM,
  RESTORE_DESTINATION_MODAL_ID,
  RESTORE_SUCCESS_TEXT,
  RETENTION_TOOLTIP,
  TABLE_FIELDS,
  buildParams,
  buildRestoreBody,
  buildRestoreConfirmMessage,
  canGoNext,
  getCauserText,
  getDefaultRange,
  getLabelText,
  getLoadFailure,
  getNextOffset,
  getPageNumber,
  getPrevOffset,
  getRangeFieldErrors,
  getRestoreMode,
  getStepBackOffset,
  getTypeBadge,
  hasRangeError,
  isPermanentlyDeleted,
  readReportRows,
  validateRange,
} from '@/helpers/deleted-report-helpers'

export default {
  name: 'DeletedReport',
  components: {
    BAlert,
    BBadge,
    BBreadcrumb,
    BButton,
    BCard,
    BCol,
    BForm,
    BFormDatepicker,
    BFormGroup,
    BRow,
    BTable,
    MoveModal,
    MyLoading,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const { currentObject } = useMyStore()
    return { currentObject }
  },
  data() {
    const { dateFrom, dateTo } = getDefaultRange()
    return {
      PAGE_TITLE,
      BREADCRUMB_ITEMS,
      TABLE_FIELDS,
      EM_DASH,
      EMPTY_TEXT,
      PERMANENT_LABEL,
      RETENTION_TOOLTIP,
      DATEPICKER_PROPS,
      RESTORE_DESTINATION_MODAL_ID,
      RESTORE_CHOOSE_HINT,
      restoreBusy: false,
      rows: [],
      loadingShow: false,
      hasLoaded: false,
      forbidden: false,
      errorText: '',
      requestSeq: 0,
      // The picker models; paging and refetches use `applied`, the filters of the last successful load.
      filters: { dateFrom, dateTo },
      applied: { dateFrom, dateTo },
      offset: 0,
      rangeErrors: { dateFrom: null, dateTo: null },
    }
  },
  computed: {
    canPrev() {
      return this.offset > 0 && !this.loadingShow
    },
    canNext() {
      return canGoNext(this.rows.length) && !this.loadingShow
    },
    pageNumber() {
      return getPageNumber(this.offset)
    },
  },
  created() {
    this.$root.$on('bv::modal::hidden', this.onModalHidden)
  },
  mounted() {
    this.loadPage({ filters: this.filters, offset: 0 })
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::hidden', this.onModalHidden)
  },
  methods: {
    getCauserText,
    getLabelText,
    getTypeBadge,
    isPermanentlyDeleted,
    formatDeletedAt(value) {
      return this.dateHelpers.format(value, DATE_FORMAT)
    },
    async loadPage({ filters, offset }) {
      // Only the latest request may write rows, panel and loading state.
      const seq = this.requestSeq + 1
      this.requestSeq = seq
      this.loadingShow = true
      const params = buildParams(filters, offset)
      try {
        const response = await objectsRepository.deletedReport(params)
        if (seq !== this.requestSeq) {
          return
        }
        const rows = readReportRows(response)
        if (rows === null) {
          this.handleLoadFailure(response)
          return
        }
        const back = getStepBackOffset(offset, rows.length)
        if (back !== null) {
          // The page emptied (its last row was removed): the nested call owns the loading flag.
          await this.loadPage({ filters, offset: back })
          return
        }
        // State is committed only after a successful reply: a failure leaves the previous page intact.
        this.rows = rows
        this.applied = { dateFrom: filters.dateFrom, dateTo: filters.dateTo }
        this.offset = offset
        this.rangeErrors = { dateFrom: null, dateTo: null }
        this.hasLoaded = true
        this.forbidden = false
        this.errorText = ''
      } catch (error) {
        if (seq !== this.requestSeq) {
          return
        }
        const rangeErrors = getRangeFieldErrors(this.apiErrors.getFieldErrors(error))
        if (hasRangeError(rangeErrors)) {
          this.rangeErrors = rangeErrors
          return
        }
        this.handleLoadFailure(error)
      } finally {
        if (seq === this.requestSeq) {
          this.loadingShow = false
        }
      }
    },
    applyFilters() {
      if (this.loadingShow) {
        return
      }
      const check = validateRange(this.filters.dateFrom, this.filters.dateTo)
      if (!check.valid) {
        this.rangeErrors = {
          dateFrom: check.field === 'dateFrom' ? check.text : null,
          dateTo: check.field === 'dateTo' ? check.text : null,
        }
        return
      }
      this.rangeErrors = { dateFrom: null, dateTo: null }
      this.loadPage({ filters: { ...this.filters }, offset: 0 })
    },
    resetFilters() {
      if (this.loadingShow) {
        return
      }
      const { dateFrom, dateTo } = getDefaultRange()
      this.filters = { dateFrom, dateTo }
      this.rangeErrors = { dateFrom: null, dateTo: null }
      this.loadPage({ filters: { ...this.filters }, offset: 0 })
    },
    goPrev() {
      if (!this.canPrev) {
        return
      }
      this.loadPage({ filters: this.applied, offset: getPrevOffset(this.offset) })
    },
    goNext() {
      if (!this.canNext) {
        return
      }
      this.loadPage({ filters: this.applied, offset: getNextOffset(this.offset) })
    },
    // Reloads the page on screen with the applied filters (plan 05-04 calls it after a restore).
    refetchCurrent() {
      return this.loadPage({ filters: this.applied, offset: this.offset })
    },
    restoreMode(row) {
      return getRestoreMode(row)
    },
    async onRestoreClick(row) {
      if (this.restoreBusy) {
        return
      }
      const body = buildRestoreBody(row)
      if (body === null) {
        return
      }
      // Raised before the dialog is awaited: a second click cannot open a second flow.
      this.restoreBusy = true
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(buildRestoreConfirmMessage(row.label), {
          title: RESTORE_CONFIRM.title,
          okTitle: RESTORE_CONFIRM.okTitle,
          cancelTitle: RESTORE_CONFIRM.cancelTitle,
          okVariant: 'danger',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        if (confirmed !== true) {
          return
        }
        await this.restoreRow(row, body)
      } finally {
        this.restoreBusy = false
      }
    },
    async restoreRow(row, body) {
      this.loadingShow = true
      try {
        const [settled] = await Promise.allSettled([objectsRepository.restore(row.id, body)])
        const summary = restoreResults.summarizeRestoreResults([row.id], [settled])
        if (summary.restoredObjects.length === 1) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Successo!',
              text: RESTORE_SUCCESS_TEXT,
              icon: 'CheckIcon',
              variant: 'success',
            },
          }, { position: 'top-center' })
        } else if (summary.singleFailure) {
          this.apiErrors.notifyApiError(summary.singleFailure, restoreResults.RESTORE_ERROR_OVERRIDES)
        }
        // The server is the source of truth: success, 404, 403 and code 0 all reload the page.
        await this.refetchCurrent()
      } finally {
        this.loadingShow = false
      }
    },
    onChooseDestination(row) {
      if (this.restoreBusy || getRestoreMode(row) !== 'choose') {
        return
      }
      this.currentObject = row.id
      this.$bvModal.show(RESTORE_DESTINATION_MODAL_ID)
    },
    onModalHidden(bvEvent, modalId) {
      if (modalId !== RESTORE_DESTINATION_MODAL_ID) {
        return
      }
      this.refetchCurrent()
    },
    handleLoadFailure(input) {
      // Only the fixed Italian texts reach the user; a failure keeps the rows already shown.
      const status = this.apiErrors.getErrorStatus(input)
      const text = this.apiErrors.notifyApiError(input, LOAD_ERROR_OVERRIDES)
      const outcome = getLoadFailure(status, this.hasLoaded)
      if (outcome.forbidden) {
        this.forbidden = true
      }
      if (outcome.panel) {
        this.errorText = text
      }
    },
  },
}
</script>

<style scoped>
.deleted-report-name {
  max-width: 250px;
  vertical-align: bottom;
}
</style>
